import { defineStyleConfig, } from '@chakra-ui/react';
import { ThemeBorderToken, ThemeColorToken, ThemeFontSizeToken, ThemeFontToken, ThemeRadiusToken, } from '../theme-tokens';
import { isTouchScreenDevice } from '../../theme-helper';
const baseStyle = {
    fontFamily: ThemeFontToken.SubHeader,
    borderRadius: ThemeRadiusToken.XLarge,
    border: ThemeBorderToken.XSmall,
    borderColor: 'transparent',
    fontWeight: 'medium',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    _disabled: {
        cursor: 'not-allowed',
        opacity: 0.48,
    },
    _active: {
        color: ThemeColorToken.OnAccent,
        background: ThemeColorToken.AccentActive,
    },
};
const variantPrimary = {
    background: ThemeColorToken.Accent,
    color: ThemeColorToken.OnAccent,
    _hover: {
        background: isTouchScreenDevice()
            ? ThemeColorToken.Accent
            : ThemeColorToken.AccentHover,
        _disabled: {
            background: ThemeColorToken.Accent,
            color: ThemeColorToken.OnAccent,
        },
    },
    _active: {
        background: ThemeColorToken.AccentActive,
        color: ThemeColorToken.OnAccent,
    },
};
const variantSecondary = {
    background: ThemeColorToken.AccentContainer,
    color: ThemeColorToken.OnAccentContainer,
    _hover: {
        background: isTouchScreenDevice()
            ? ThemeColorToken.AccentContainer
            : ThemeColorToken.AccentHover,
        color: isTouchScreenDevice()
            ? ThemeColorToken.OnAccentContainer
            : ThemeColorToken.OnAccent,
        _disabled: {
            background: ThemeColorToken.AccentContainer,
            color: ThemeColorToken.OnAccentContainer,
        },
    },
    _active: {
        background: ThemeColorToken.AccentActive,
        color: ThemeColorToken.OnAccent,
    },
};
const variantGhost = {
    color: ThemeColorToken.OnBackground,
    fontWeight: 'medium',
    _hover: {
        color: ThemeColorToken.OnBackground,
        bg: isTouchScreenDevice() ? 'none' : ThemeColorToken.AccentHoverAlpha,
        textDecoration: 'none',
    },
    _active: {
        bg: ThemeColorToken.AccentActiveAlpha,
        color: ThemeColorToken.OnBackground,
    },
};
const variantLink = {
    color: ThemeColorToken.OnBackground,
    fontFamily: ThemeFontToken.Body,
    borderRadius: 0,
    fontWeight: 'normal',
    background: 'transparent',
    textDecorationLine: 'underline',
    textUnderlineOffset: '3px',
    width: 'fit-content',
    border: 0,
    _hover: {
        color: isTouchScreenDevice()
            ? ThemeColorToken.OnBackground
            : ThemeColorToken.AccentHover,
    },
    _active: {
        color: ThemeColorToken.AccentActive,
        background: 'transparent',
    },
    _disabled: {
        textDecoration: 'none',
    },
};
const variantSegmentedControl = {
    flex: '1',
    fontWeight: 'normal',
    font: ThemeFontToken.Body,
    bg: ThemeColorToken.AccentContainer,
    color: ThemeColorToken.OnAccentContainer,
    borderRadius: '2px',
    _hover: {
        textDecoration: 'underline',
        _disabled: {
            textDecoration: 'none',
        },
    },
    // [data-active] is the controlled 'isActive'-state of SegmentedControl
    '&[data-active]': {
        fontWeight: 'medium',
        bg: ThemeColorToken.Surface,
        color: ThemeColorToken.OnSurface,
        ':disabled': {
            bg: ThemeColorToken.Surface,
        },
        ':hover:disabled': {
            bg: ThemeColorToken.Surface,
        },
    },
    // Native active selctor, seen when button is in pressed state
    _active: {
        bg: ThemeColorToken.AccentHoverAlpha,
        color: ThemeColorToken.OnAccentContainer,
    },
};
const variantTertiary = {
    background: 'transparent',
    color: ThemeColorToken.OnBackground,
    borderColor: ThemeColorToken.OnBackgroundBorder,
    _active: {
        borderColor: 'transparent',
        background: ThemeColorToken.AccentActiveAlpha,
        color: ThemeColorToken.OnBackground,
        _disabled: {
            background: 'transparent',
            borderColor: ThemeColorToken.OnBackgroundBorder,
            color: ThemeColorToken.OnBackground,
        },
    },
    '@media(hover: hover)': {
        _hover: {
            borderColor: 'transparent',
            color: ThemeColorToken.OnBackground,
            background: ThemeColorToken.AccentHoverAlpha,
            _disabled: {
                background: 'transparent',
                borderColor: ThemeColorToken.OnBackgroundBorder,
                color: ThemeColorToken.OnBackground,
            },
        },
    },
};
// Toggle variant (only used by the ToggleButton component)
const pressedSelector = '&[aria-pressed="true"]';
const notPressedSelector = '&[aria-pressed="false"]';
const variantToggle = {
    // Base style
    borderRadius: ThemeRadiusToken.Small,
    borderWidth: ThemeBorderToken.XSmall,
    borderColor: ThemeColorToken.OnSurfaceBorder,
    // Pressed state
    [`${pressedSelector}`]: {
        background: ThemeColorToken.Accent,
        color: ThemeColorToken.OnAccent,
    },
    // Not pressed state
    [`${notPressedSelector}`]: {
        background: ThemeColorToken.Surface,
        color: ThemeColorToken.OnSurface,
    },
    // Hover styles
    '@media(hover: hover)': {
        [`${pressedSelector}&:hover`]: {
            background: ThemeColorToken.AccentHover,
            borderColor: ThemeColorToken.AccentHover,
            _disabled: {
                background: ThemeColorToken.Accent,
                borderColor: ThemeColorToken.Accent,
            },
        },
        [`${notPressedSelector}&:hover`]: {
            background: ThemeColorToken.AccentHoverAlpha,
            color: ThemeColorToken.OnSurface,
            _disabled: {
                background: ThemeColorToken.Surface,
                color: ThemeColorToken.OnSurface,
            },
        },
    },
    // Active styles
    [`${pressedSelector}:active`]: {
        color: ThemeColorToken.OnAccent,
        background: ThemeColorToken.AccentActive,
    },
    [`${notPressedSelector}:active`]: {
        color: ThemeColorToken.OnSurface,
        background: ThemeColorToken.AccentActiveAlpha,
    },
    // Define general disabled styles last to ensure they are not overridden
    [`${pressedSelector}:disabled`]: {
        color: ThemeColorToken.OnAccent,
        background: ThemeColorToken.Accent,
        borderColor: ThemeColorToken.Accent,
    },
    [`${notPressedSelector}:disabled`]: {
        color: ThemeColorToken.OnSurface,
        background: ThemeColorToken.Surface,
    },
};
export const buttonConfig = defineStyleConfig({
    baseStyle,
    sizes: {
        lg: {
            px: '24px',
            height: '56px',
            minWidth: '56px',
            fontSize: ThemeFontSizeToken.Large,
        },
        md: {
            px: '16px',
            height: '40px',
            minWidth: '40px',
            fontSize: ThemeFontSizeToken.Medium,
        },
        sm: {
            px: '8px',
            height: '24px',
            minWidth: '24px',
            fontSize: ThemeFontSizeToken.Small,
        },
    },
    variants: {
        primary: variantPrimary,
        secondary: variantSecondary,
        ghost: variantGhost,
        link: variantLink,
        segmentedControl: variantSegmentedControl,
        tertiary: variantTertiary,
        toggle: variantToggle,
    },
});
//# sourceMappingURL=button-theme.js.map